import { hasCopy, translate } from '@ticarishift/i18n';

/**
 * Platform yetki anahtarları (07 §3.12; sunucuda `src/Shared/Access/PlatformPermissions.cs`). Oturumun yetkileri
 * `GET /api/platform/me`'den gelir; buradaki sabitler yalnız arayüzün sorduğu anahtarların adlarıdır (menü, eylem, 403
 * ekranındaki yetki adı).
 */
export const PlatformPermissions = {
  TenantsRead: 'platform.tenants.read',
  TenantsManage: 'platform.tenants.manage',
  TenantsClose: 'platform.tenants.close',
  EntitlementsManage: 'platform.entitlements.manage',
  UsersManage: 'platform.users.manage',
  AuditRead: 'platform.audit.read',
  SystemRead: 'platform.system.read',
  OutboxReplay: 'platform.outbox.replay',
  /** Faturalama izleme ve gelir göstergeleri (S43): platform sahibi, yöneticisi ve faturalama şablonu. */
  BillingRead: 'platform.billing.read',
  /** Havale kaydı, iade, elle yeniden tahsil, uyarı kapatma, sahte sağlayıcı ret işareti (S43; platform sahibi ve yöneticisi). */
  BillingManage: 'platform.billing.manage',
  /** Yasal parametre sürümleri ve resmî tatil takvimi (S23): görme (platform sahibi, yöneticisi ve destek). */
  LegalRead: 'platform.legal.read',
  /** Taslak açma, düzenleme, senaryo, etki önizlemesi ve yayın (platform sahibi ve yöneticisi; ADR-002 §2.2). */
  LegalManage: 'platform.legal.manage',
  /** Sahip hesap kurtarma (S31, YH-S52): talep açma ve görme (platform sahibi, yöneticisi ve destek). */
  OwnerRecoveryRequest: 'platform.owner_recovery.request',
  /** Talebi onaylama ya da reddetme (platform sahibi ve yöneticisi; talebi açan operatör karar veremez, S31-K2). */
  OwnerRecoveryDecide: 'platform.owner_recovery.decide',
  /** Sistem duyurusu ve bakım bildirimi (S40): yazma, yayın ve iptal (platform sahibi ve yöneticisi); görmek `platform.system.read`. */
  AnnouncementsManage: 'platform.announcements.manage',
  /**
   * Plan kataloğu ve abonelik (S41, YH-S57): plan ve ek paket sürümü yayımlama, işletmeye plan atama, işletmeye özel Kurumsal plan
   * (platform sahibi ve yöneticisi). Kataloğu ve aboneliği görmek `platform.tenants.read`'tir.
   */
  PlansManage: 'platform.plans.manage',
  /**
   * Destek erişimi (S44, ADR-002 §2.4): işletmenin izniyle salt okuma destek oturumu başlatma, panele geçiş, bitirme; izinli işletmeler
   * ve oturum geçmişi (platform sahibi, yöneticisi ve destek).
   */
  SupportSession: 'platform.support.session',
  /** E-posta (SMTP) ayarı (S62-d, SK-21): görme, gerekçeli kayıt ve deneme e-postası (yalnız platform sahibi). */
  EmailSettingsManage: 'platform.settings.email',
} as const;

export type PlatformPermission = (typeof PlatformPermissions)[keyof typeof PlatformPermissions];

/** Davet ve rol değiştirmede seçilebilen dört platform rol şablonu (ADR-002 §2.2; `RoleTemplateCodes.Platform`). */
export const PLATFORM_ROLES = ['platform_owner', 'platform_admin', 'platform_support', 'platform_billing_read'] as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export function isPlatformRole(value: string): value is PlatformRole {
  return (PLATFORM_ROLES as readonly string[]).includes(value);
}

/** Rolün adı ("Platform sahibi"); bilinmeyen kod olduğu gibi yazılır. */
export function roleLabel(role: string): string {
  const key = `backoffice.roles.${role}`;
  return hasCopy(key) ? translate(key) : role;
}

export function roleDescription(role: PlatformRole): string {
  return translate(`backoffice.roleDescriptions.${role}`);
}

/** Yetkinin adı (403 ekranı). */
export function permissionLabel(permission: PlatformPermission): string {
  const key = `backoffice.permissions.${permission}`;
  return hasCopy(key) ? translate(key) : permission;
}
